<template>
  <div>
    <q-card
      class="column cursor-pointer"
      :class="{
        [`bg-${backgroundColor}`]: backgroundColor !== 'custom',
        [`text-${textColor}`]: textColor !== 'custom',
        'bg-transparent': !backgroundColor || backgroundColor === 'default',
        [defaultTextColor]: !textColor || textColor === 'default',
      }"
      :style="{
        'background-color': backgroundColor === 'custom' ? backgroundColorHex : '',
        'color': textColor === 'custom' ? textColorHex : '',
        'border-radius': `${borderRadius}px`,
        'margin-top': showImage ? '30px' : '0px',
        'min-height': showImage ? undefined : '74px',
      }"
      flat
      bordered
      :dark="(!textColor || textColor === 'default') && defaultTextColor === 'text-white'"
      @click="handleClick"
    >
      <q-card-section
        v-if="showImage"
        :class="{ 'bg-primary': !giftCardSale.color }"
        :style="{
          background: giftCardSale.color || undefined,
          'border-radius': `${borderRadius}px ${borderRadius}px ${borderRadius + 8}px ${borderRadius + 8}px`
        }"
      >
        <img
          class="rounded-borders shadow-3 full-width"
          style="margin-top: -30px"
          :src="giftCardSaleImageUrl(giftCardSale.image, giftCardSale.color) || currentSite.giftCardImageUrl"
        >
      </q-card-section>
      <q-card-section
        class="column q-px-md"
      >
        <div
          class="text-h6 col"
          :class="{
            'text-center': !showImage,
            'text-left': showImage
          }"
        >
          {{ showName ? giftCardSale.name : '' }}
          <i18n-n
            v-if="showPrice"
            :value="giftCardSale.amount"
            format="currency"
            scope="global"
          />
          <template v-else-if="!showName">
            {{ $tc('collection.other', 1) }}
          </template>
        </div>

        <div
          v-if="(showImage || !!count) && showActionButtons"
          class="row items-center"
          :class="{
            'q-gutter-sm q-mt-sm justify-end': showImage,
            'full-width q-mt-sm': !showImage
          }"
        >
          <q-btn
            v-if="!count"
            class="col-auto text-capitalize"
            :label="$t('actions.add')"
            :padding="showImage ? 'sm' : 'xs'"
            :size="showImage ? 'md' : 'sm'"
            square
            style="background-color: rgba(0, 0, 0, 0.05)"
            :style="{ 'border-radius': `${borderRadius}px` }"
            @click.stop="increaseCount"
          />
          <q-input
            v-else
            v-model.number="selectedCount"
            class="col-auto"
            input-class="text-center"
            :input-style="{ width: showImage ? '20px' : undefined }"
            dense
            :filled="!showImage"
            :style="{ 'border-radius': `${borderRadius}px` }"
            :dark="useDarkMode"
            @update:model-value="handleInput"
          >
            <template #prepend>
              <q-btn
                :style="{ 'border-radius': `${borderRadius}px` }"
                dense
                flat
                icon="fal fa-minus"
                :disabled="selectedCount === 0"
                @click.stop="decreaseCount"
              />
            </template>
            <template #append>
              <q-btn
                :style="{ 'border-radius': `${borderRadius}px` }"
                dense
                flat
                icon="fal fa-plus"
                :disabled="selectedCount >= 10"
                @click.stop="increaseCount"
              />
            </template>
          </q-input>
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>

<script>
import { giftCardSaleImageUrl } from 'src/models/gift-card/GiftCardSale'

export default {
  name: 'GiftCardSaleCard',
  configuration: ['currentSite', 'currentCircuit'],
  props: {
    giftCardSale: {
      type: Object,
      required: true
    },
    count: {
      type: Number,
      required: false,
      default: 0
    },
    showPrice: {
      type: Boolean,
      required: false,
      default: true
    },
    backgroundColor: {
      type: String,
      required: false,
      default: 'default'
    },
    textColor: {
      type: String,
      required: false,
      default: 'default'
    },
    backgroundColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    textColorHex: {
      type: String,
      required: false,
      default: '#000000'
    },
    borderRadius: {
      type: Number,
      required: false,
      default: 4
    },
    showImage: {
      type: Boolean,
      required: false,
      default: true
    },
    showName: {
      type: Boolean,
      required: false,
      default: true
    },
    showActionButtons: {
      type: Boolean,
      required: false,
      default: true
    },
    defaultTextColor: {
      type: String,
      required: false,
      default: null
    }
  },
  emits: ['remove', 'add'],
  data () {
    return {
      selectedCount: this.count
    }
  },
  computed: {
    useDarkMode () {
      return ((!this.backgroundColor || this.backgroundColor === 'default') && this.defaultTextColor === 'text-white') ||
        (this.backgroundColor === 'custom' && this.$isColorDark(this.backgroundColorHex)) ||
        (!!this.backgroundColor && this.backgroundColor !== 'custom' && this.backgroundColor !== 'default' && this.$getTextColor(this.currentSite, this.backgroundColor) === 'text-white')
    }
  },
  created () {
    _.extend(this, { giftCardSaleImageUrl })
  },
  methods: {
    handleClick () {
      if (this.showActionButtons) {
        this.increaseCount()
      } else {
        this.$emit('add', 1)
      }
    },
    handleInput (val) {
      let parsed = parseInt(this.selectedCount, 10)

      // If parsed is not a valid number or is negative, default to 0
      if (isNaN(parsed) || parsed < 0) {
        parsed = 0
      } else if (parsed > 10) {
        parsed = 10
      }

      // Ensure selectedCount is stored as a number
      if (parsed !== this.selectedCount) {
        this.selectedCount = parsed
      }

      if (parsed > this.count) {
        this.$emit('add', parsed)
      } else if (parsed < this.count) {
        this.$emit('remove', parsed)
      }
    },
    increaseCount () {
      this.selectedCount++
      this.handleInput()
    },
    decreaseCount () {
      if (this.selectedCount > 0) {
        this.selectedCount--
        this.handleInput()
      }
    }
  }
}

</script>
<style>
.min-height-100 {
  min-height: 100px;
}
</style>
